import { useParams, useNavigate } from 'react-router-dom'
import {
  Card,
  CardBody,
  Badge,
  Button,
  Row,
  Col,
  Input,
  Alert,
} from 'reactstrap'
import {
  ArrowLeft,
  Award,
  Calendar,
  CheckCircle,
  FileText,
  Link,
  Printer,
  ExternalLink,
} from 'react-feather'
import BreadCrumbs from 'components/breadcrumbs'
import { URL_EVENT, URL_EVENT_CERTIFICATE } from 'utils/pathUrl'
import { useState } from 'react'
import { formatDateOnlyPMB } from 'utils/formatDate'

const dummyCertDetail = {
  id: 'PMKR-2024-TRAIN-001',
  title: 'Sertifikat Penyelesaian Pelatihan',
  owner: 'Pak Mangku',
  issueDate: '2024-10-15',
  expiryDate: '2025-11-02',
  verifiedBy: 'Jane Smith, AgriCert Indonesia',
  verificationDate: '2024-10-15',
  organization: 'PasarMIKRO',
  language: 'Bahasa Indonesia',
  verificationLink: 'https://pasarmikro.com/cert/PMKR-2024-TRAIN-001',
  description:
    'Pelatihan tentang Penanganan Aman yang diselenggarakan oleh PasarMIKRO pada 14 Oktober 2024. Sertifikat ini menegaskan bahwa peserta telah berhasil menyelesaikan pelatihan komprehensif tentang praktik penanganan aman bahan kimia dan hasil pertanian, termasuk penggunaan APD yang benar, protokol penyimpanan, serta prosedur darurat.',
}

const EventCertificateDetail = () => {
  const { id } = useParams()
  const navigate = useNavigate()
  const cert = dummyCertDetail
  const [copied, setCopied] = useState(false)

  const isExpired = new Date(cert.expiryDate).getTime() < new Date().getTime()

  const handleCopy = () => {
    navigator.clipboard.writeText(cert.verificationLink)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  return (
    <div className="px-3 pb-3 w-100">
      <div className="d-flex align-items-start gap-2 pt-3">
        <BreadCrumbs
          breadCrumbActive={'Detail Sertifikat'}
          breadCrumbParent2={'Sertifikat Event Digital'}
          breadCrumbParent2Link={URL_EVENT_CERTIFICATE}
          breadCrumbParent={'Event'}
          breadCrumbParentLink={URL_EVENT}
        />
      </div>

      <h5 className="fw-bold text-success mb-3">Sertifikat Digital</h5>

      <Row>
        {/* Kolom Kiri: Tampilan Sertifikat */}
        <Col md="8">
          <Card className="shadow-sm border border-success-subtle">
            <CardBody className="text-center">
              <div className="mb-3">
                <Award size={38} className="text-success" />
                <h6 className="fw-bold mt-2 text-muted">{cert.organization}</h6>
              </div>

              <h5 className="fw-bold">{cert.title}</h5>

              <p className="mb-0 text-muted">Menyatakan bahwa</p>
              <h4 className="fw-semibold text-success my-2">{cert.owner}</h4>

              <p className="text-muted small px-4">{cert.description}</p>

              <div className="border rounded-pill py-1 px-3 mt-3 bg-light small d-inline-block">
                Diverifikasi oleh {cert.verifiedBy} pada{' '}
                {formatDateOnlyPMB(cert.verificationDate)}
              </div>

              <Alert color="light-success" className="mt-4 text-start small">
                <Printer size={16} className="me-1" />
                Format Siap Cetak — Sertifikat ini dapat diunduh dalam format
                PDF ukuran A4 untuk hasil terbaik.
              </Alert>
            </CardBody>
          </Card>
        </Col>

        {/* Kolom Kanan: Detail Sertifikat */}
        <Col md="4">
          <Card className="shadow-sm border-0 mb-2">
            <CardBody>
              <h6 className="fw-bold mb-3">Detail Sertifikat</h6>
              <div className="small text-muted mb-2">ID Sertifikat</div>
              <Input
                value={cert.id}
                readOnly
                disabled
                className="mb-3"
                data-testid="event-certificate-id-input"
              />

              <div className="small text-muted">Tanggal Terbit</div>
              <p className="fw-semibold mb-2">
                {formatDateOnlyPMB(cert.issueDate)}
              </p>

              <div className="small text-muted">Tanggal Kedaluwarsa</div>
              <p className="fw-semibold mb-2">
                {formatDateOnlyPMB(cert.expiryDate)}{' '}
                <Badge color={isExpired ? 'light-danger' : 'light-success'}>
                  {isExpired ? 'Kedaluwarsa' : 'Masih Berlaku'}
                </Badge>
              </p>

              <div className="small text-muted">Bahasa</div>
              <p className="fw-semibold mb-3">{cert.language}</p>

              <Alert
                color="light-success"
                className="small p-2 d-flex align-items-center"
              >
                <CheckCircle size={16} className="me-2 text-success" />
                Sertifikat Terverifikasi — Sertifikat ini telah diverifikasi dan
                autentik.
              </Alert>

              <hr />

              <div>
                <div className="small text-muted mb-1">Bagikan Sertifikat</div>
                <div className="d-flex gap-1 mb-2">
                  <Input
                    value={cert.verificationLink}
                    readOnly
                    data-testid="event-certificate-link-input"
                  />
                  <Button
                    color="success"
                    onClick={handleCopy}
                    data-testid="event-certificate-copy-button"
                  >
                    <Link size={14} />
                  </Button>
                </div>
                {copied && (
                  <div className="text-success small">
                    Tautan berhasil disalin!
                  </div>
                )}
              </div>

              <div className="mt-3">
                <Button
                  color="success"
                  outline
                  className="w-100 mb-2"
                  onClick={() =>
                    alert('Mengunduh sertifikat dalam format PDF...')
                  }
                  data-testid="event-certificate-download-button"
                >
                  <FileText size={14} className="me-1" />
                  Unduh PDF
                </Button>

                <Button
                  color="light"
                  className="w-100"
                  onClick={() => navigate('/event/detail')}
                  data-testid="event-certificate-view-event-button"
                >
                  <ExternalLink size={14} className="me-1" />
                  Lihat Event Terkait
                </Button>
              </div>
            </CardBody>
          </Card>
        </Col>
      </Row>
    </div>
  )
}

export default EventCertificateDetail
